import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search } from "lucide-react";
import { PageHeader, Badge, EmptyState, statusTone } from "@/components/Common";

/**
 * Generic resource page.
 * Props:
 *  - endpoint: "/assets"
 *  - eyebrow, title, subtitle, ctaLabel
 *  - fields: [{name, label, type: text|textarea|select|number, options?, required?, default?}]
 *  - columns: [{key, label, render?: (item) => ReactNode}]
 *  - testidPrefix
 *  - searchKeys: array of field names to filter on (default: all string fields)
 */
export default function ResourcePage({
  endpoint, eyebrow, title, subtitle, ctaLabel,
  fields, columns, testidPrefix, searchKeys, defaultForm,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const initial = defaultForm || fields.reduce((acc, f) => {
    acc[f.name] = f.default ?? (f.type === "number" ? 0 : "");
    return acc;
  }, {});
  const [form, setForm] = useState(initial);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(endpoint);
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint, refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // strip empty optional values
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        const fld = fields.find((f) => f.name === k);
        if (fld?.type === "number") {
          const n = parseFloat(payload[k]);
          payload[k] = isNaN(n) ? 0 : n;
        }
      });
      await api.post(endpoint, payload);
      toast.success("Created");
      setOpen(false);
      setForm(initial);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = items.filter((it) => {
    if (!q) return true;
    const keys = searchKeys || Object.keys(it);
    return keys.some((k) =>
      String(it[k] || "").toLowerCase().includes(q.toLowerCase())
    );
  });

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid={`${testidPrefix}-new-button`}
              >
                <Plus size={16} className="mr-1.5" /> {ctaLabel || "New record"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">{ctaLabel || "New record"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid={`${testidPrefix}-form`}>
                {fields.map((f) => (
                  <div key={f.name}>
                    <Label>{f.label}{f.required && <span className="text-red-600 ml-1">*</span>}</Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        required={f.required}
                        rows={3}
                        value={form[f.name] || ""}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        className="mt-1"
                        data-testid={`${testidPrefix}-${f.name}-input`}
                      />
                    ) : f.type === "select" ? (
                      <Select
                        value={form[f.name] || ""}
                        onValueChange={(v) => setForm({ ...form, [f.name]: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid={`${testidPrefix}-${f.name}-select`}>
                          <SelectValue placeholder={`Choose ${f.label}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {(f.options || []).map((opt) => (
                            <SelectItem key={typeof opt === "string" ? opt : opt.value} value={typeof opt === "string" ? opt : opt.value}>
                              {typeof opt === "string" ? opt.replaceAll("_", " ") : opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        required={f.required}
                        value={form[f.name] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        className="mt-1"
                        data-testid={`${testidPrefix}-${f.name}-input`}
                        placeholder={f.placeholder}
                      />
                    )}
                  </div>
                ))}
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid={`${testidPrefix}-submit-button`}
                  >
                    {busy ? "Saving…" : "Save record"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid={`${testidPrefix}-search-input`}
          />
        </div>
        <span className="text-xs text-gray-500">{filtered.length} record(s)</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No records yet" hint="Click the button above to create the first one." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid={`${testidPrefix}-table`}>
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 text-gray-700 align-top">
                        {c.render ? c.render(it) : it[c.key] || "—"}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(it.id)}
                        data-testid={`${testidPrefix}-delete-${it.id}`}
                        className="text-red-700 hover:text-red-800 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export { Badge, statusTone };
